import {
  CargoSubtype,
  containerLevelHistoryTypes,
  ContainerStatusEntity
} from "@ots-frontend/ots-data";
import {useMantineTheme} from "@mantine/core";
import type {DataGridColumnDefinition} from "@ots-frontend/ots-ui-tw";
import {useTranslation} from "react-i18next";
import {useMemo} from "react";
import {GridCell, GridCellKind} from "@glideapps/glide-data-grid";
import {getCargoSubtypeColor} from "../../../util/cargo-subtype-ui-util";
import {SparklineCell} from "@glideapps/glide-data-grid-cells/dist/ts/cells/sparkline-cell";
import {getDisplayableHeightValue, getDisplayableLevelValue} from "../../../util/container-level-ui-util";
import {ContainerStatusDataGridProps} from "./ContainerStatusDataGrid";
import {getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {TagsCell} from "@glideapps/glide-data-grid-cells/dist/ts/cells/tags-cell";
import {adjustBackgroundForTextContrast} from "../../../theme/colors";
import {resolveContainerStatusIcons, useContainerStatusIconSet} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";

/**
 * Columns whose cells are replaced by one spanning message when a container's readings were omitted. The span is
 * worked out after the saved layout is applied, over whichever of these columns are visible and adjacent.
 */
export type ContainerStatusMeasurementGroup = "fill" | "load";

export type ContainerStatusColumn = DataGridColumnDefinition<ContainerStatusEntity> & {
  measurementGroup?: ContainerStatusMeasurementGroup
}

/** Stable column IDs; saved layouts refer to these, so don't rename them. */
export const containerStatusColumnId = {
  company: "COMPANY",
  route: "ROUTE",
  site: "SITE",
  container: "CONTAINER",
  status: "STATUS",
  tags: "TAGS",
  history: "HISTORY",
  poll: "POLL",
  fill: "FILL",
  cargoFill: (cargoSubtype: CargoSubtype) => `FILL_${cargoSubtype.code.toUpperCase()}`,
  timeToFull: "TIME_TO_FULL",
  lastDispatch: "LAST_DISPATCH",
  lastHaul: "LAST_HAUL",
  availableLoads: "AVAILABLE_LOADS",
  availableLoadVolume: "AVAILABLE_LOAD_VOLUME",
  cargoVolume: (cargoSubtype: CargoSubtype) => `VOLUME_${cargoSubtype.code.toUpperCase()}`,
  cargoHeight: (cargoSubtype: CargoSubtype) => `HEIGHT_${cargoSubtype.code.toUpperCase()}`,
  totalHeight: "TOTAL_HEIGHT",
  siteAvailableLoadVolume: "SITE_AVAILABLE_LOAD_VOLUME",
  operatingArea: "OPERATING_AREA",
  assignedHauler: "ASSIGNED_HAULER",
} as const

const CRITICAL_CELL = {bgCell: "#E6B8B7FF"}
const STALE_POLL_CELL = {
  light: {textDark: "#B45309", baseFontStyle: "600 13px"},
  dark: {textDark: "#FBBF24", baseFontStyle: "600 13px"},
}

const getHistoryContent = (cargoSubtype: CargoSubtype) => {
  return (status: ContainerStatusEntity): GridCell => {
    const values = (status?.getHistory(cargoSubtype, containerLevelHistoryTypes.meter) ?? [])
      .map(l => l.volume)
      .filter((item): item is number => item !== undefined && item !== null)

    return {
      kind: GridCellKind.Custom,
      allowOverlay: false,
      copyData: values.join(", "),
      data: {
        kind: "sparkline-cell",
        values,
        displayValues: values.map(x => Math.round(x).toString()),
        color: getCargoSubtypeColor(cargoSubtype),
        yAxis: [0, status?.containerVolume ?? 400],
      }
    } as SparklineCell
  }
}

const formatFill = (rate: number | undefined, percent: number | undefined): string => {
  if (rate && percent) {
    return rate.toFixed(2) + "/hr (" + (percent * 100).toFixed(2) + "%)"
  } else if (rate) {
    return rate.toFixed(2) + "/hr"
  } else if (percent) {
    return (percent * 100).toFixed(2) + "%"
  } else {
    return ""
  }
}

// a cargo's own fill; meterFill is the container total, with rates added across cargos
const getCargoFill = (status: ContainerStatusEntity | undefined, cargoSubtype: CargoSubtype) =>
  status?.getFill(cargoSubtype, containerLevelHistoryTypes.meter)

export type UseContainerStatusDataGridColsProps = Pick<ContainerStatusDataGridProps, 'colWidths' | 'colOptions' | 'cargoSubtypes'> & {
  /** Flags the Poll cell and adds the stale poll status icon. */
  isStalePoll?: (status: ContainerStatusEntity | undefined) => boolean
  /** The grid's own scheme, which follows OtsThemeRoot; Mantine's when missing. */
  colorScheme?: "light" | "dark"
}

export type UseContainerStatusDataGridResult = {
  colsMap: ContainerStatusColumn[]
}

export function useContainerStatusDataGridCols(props: UseContainerStatusDataGridColsProps): UseContainerStatusDataGridResult {
  const {colWidths, colOptions, cargoSubtypes, isStalePoll, colorScheme: gridColorScheme} = props;

  const {t} = useTranslation(["common"])

  const mantineColorScheme = useMantineTheme().colorScheme
  const colorScheme = gridColorScheme ?? mantineColorScheme
  const contextIconSet = useContainerStatusIconSet()
  const iconSet = colOptions?.statusIconSet ?? contextIconSet

  const colsMap = useMemo(() => {
    const cols: ContainerStatusColumn[] = []
    const cargos = Array.from(cargoSubtypes ?? [])
    const primaryCargo = cargos[0]
    const cargoName = (cargoSubtype: CargoSubtype) => t(cargoSubtype.displayValue.id, cargoSubtype.displayValue.defaultMessage)

    // legacy positional widths seed the first saved layout; they only cover the columns that used to be shown
    let colIndex = 0
    const width = (fallback: number) => colWidths?.at(colIndex++) ?? fallback

    cols.push({
      id: containerStatusColumnId.company,
      title: t("common.entity.company", "Company"),
      defaultVisible: colOptions?.showCompanyCol === true,
      width: colOptions?.showCompanyCol ? width(100) : 100,
      hasMenu: true,
      getSortValue: (status) => status?.siteCompanyName,
      getContent: (status) => ({
        kind: GridCellKind.Text,
        data: status?.siteCompanyName ?? "",
        allowOverlay: false,
        displayData: status?.siteCompanyName ?? "",
      })
    }, {
      id: containerStatusColumnId.route,
      title: t("common.entity.route", "Route"),
      width: width(100),
      hasMenu: true,
      getSortValue: (status) => status?.siteRouteName,
      getContent: (status) => ({
        kind: GridCellKind.Text,
        data: status?.siteRouteName ?? "",
        allowOverlay: false,
        displayData: status?.siteRouteName ?? "",
      })
    }, {
      id: containerStatusColumnId.site,
      title: t("common.entity.site", "Site"),
      width: width(300),
      hasMenu: true,
      getSortValue: (status) => status?.siteName,
      getContent: (status) => ({
        kind: GridCellKind.Text,
        data: status?.siteName ?? "",
        allowOverlay: false,
        displayData: status?.siteName ?? "",
      })
    }, {
      id: containerStatusColumnId.container,
      title: t("common.entity.container", "Container"),
      width: width(300),
      hasMenu: true,
      getSortValue: (status) => status?.containerName,
      getContent: (status) => ({
        kind: GridCellKind.Text,
        data: status?.containerName ?? "",
        allowOverlay: false,
        displayData: status?.containerName ?? "",
      })
    }, {
      id: containerStatusColumnId.status,
      title: "",
      columnLabel: t("entity.containerstatus.datagrid.column.status", "Status"),
      width: width(85),
      hasMenu: true,
      sortable: false,
      getContent: (status) => {
        const icons = resolveContainerStatusIcons(status, colOptions?.statusIconVariant ?? "filled", iconSet, colorScheme, {
          stalePoll: isStalePoll?.(status),
        })
        const data = icons.map(icon => icon.src)
        const hasMessages = icons.some(icon => icon.name === "message")

        return {
          kind: GridCellKind.Image,
          data: data,
          allowOverlay: false,
          allowAdd: false,
          cursor: hasMessages ? "pointer" : undefined,
        }
      }
    }, {
      id: containerStatusColumnId.tags,
      title: "Tags",
      width: width(85),
      hasMenu: true,
      sortable: false,
      getContent: (status) => {
        const possibleTags = status?.containerTags?.map(t => ({
          tag: t.label,
          color: adjustBackgroundForTextContrast("#000000", t.colorHex ?? "gray")
        })) ?? []
        const tags = possibleTags.map(t => t.tag)

        return {
          kind: GridCellKind.Custom,
          allowOverlay: false,
          readonly: true,
          copyData: tags.join(", "),
          data: {
            kind: "tags-cell",
            possibleTags: possibleTags,
            tags: tags
          }
        } as TagsCell
      }
    })

    if (primaryCargo) {
      cols.push({
        id: containerStatusColumnId.history,
        title: t('entity.containerstatus.datagrid.column.history', 'History'),
        width: width(100),
        hasMenu: true,
        sortable: false,
        measurementGroup: "fill",
        getContent: getHistoryContent(primaryCargo)
      })
    }

    cols.push({
      id: containerStatusColumnId.poll,
      title: t('entity.containerstatus.datagrid.column.dttm', 'Poll'),
      width: width(100),
      hasMenu: true,
      measurementGroup: "fill",
      getSortValue: (status) => status?.meterFill?.dttm,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.meterFill?.dttm,
        allowOverlay: false,
        displayData: status?.meterFill?.dttm ? getFormattedDateTime(status.meterFill.dttm) : "",
        themeOverride: isStalePoll?.(status) ? STALE_POLL_CELL[colorScheme === "dark" ? "dark" : "light"] : undefined,
      })
    }, {
      id: containerStatusColumnId.fill,
      title: t('entity.containerstatus.datagrid.column.fill', 'Fill'),
      width: width(115),
      hasMenu: true,
      measurementGroup: "fill",
      getSortValue: (status) => status?.meterFill?.actualPercent,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.meterFill?.actualPercent ?? Number.MIN_SAFE_INTEGER,
        allowOverlay: false,
        displayData: formatFill(status?.meterFill?.rate, status?.meterFill?.actualPercent),
        themeOverride: status?.meterFill?.actualStatus === "CRITICAL" ? CRITICAL_CELL : undefined
      })
    })

    for (const cargoSubtype of cargos) {
      cols.push({
        id: containerStatusColumnId.cargoFill(cargoSubtype),
        title: cargoName(cargoSubtype) + " " + t('entity.containerstatus.datagrid.column.fill', 'Fill'),
        defaultVisible: false,
        width: 115,
        hasMenu: true,
        measurementGroup: "fill",
        getSortValue: (status) => getCargoFill(status, cargoSubtype)?.rate,
        getContent: (status) => {
          const fill = getCargoFill(status, cargoSubtype)
          return {
            kind: GridCellKind.Number,
            data: fill?.rate ?? Number.MIN_SAFE_INTEGER,
            allowOverlay: false,
            displayData: formatFill(fill?.rate, fill?.actualPercent),
            themeOverride: fill?.actualStatus === "CRITICAL" ? CRITICAL_CELL : undefined
          }
        }
      })
    }

    cols.push({
      id: containerStatusColumnId.timeToFull,
      title: t('entity.containerstatus.datagrid.column.timetofull', 'Time To Full'),
      width: width(115),
      hasMenu: true,
      measurementGroup: "fill",
      getSortValue: (status) => status?.meterFill?.actualTimeToFull,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.meterFill?.actualTimeToFull ?? Number.MAX_SAFE_INTEGER,
        allowOverlay: false,
        displayData: status?.meterFill?.actualTimeToFull?.toFixed(2) ?? "",
        themeOverride: status?.meterFill?.actualTimeToFullCritical ? CRITICAL_CELL : undefined
      })
    }, {
      id: containerStatusColumnId.lastDispatch,
      title: t('entity.containerstatus.datagrid.column.dispatch', 'Last Dispatch'),
      defaultVisible: colOptions?.showDispatchCol === true,
      width: colOptions?.showDispatchCol ? width(40) : 120,
      hasMenu: true,
      getSortValue: (status) => status?.latestDispatchDttm,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.latestDispatchDttm,
        allowOverlay: false,
        displayData: status?.latestDispatchDttm ? getFormattedDateTime(status.latestDispatchDttm) : "",
      })
    }, {
      id: containerStatusColumnId.lastHaul,
      title: t('entity.containerstatus.datagrid.column.pickup', 'Last Haul'),
      defaultVisible: colOptions?.showPickupCol === true,
      width: colOptions?.showPickupCol ? width(40) : 120,
      hasMenu: true,
      getSortValue: (status) => status?.latestPickupDttm,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.latestPickupDttm,
        allowOverlay: false,
        displayData: status?.latestPickupDttm ? getFormattedDateTime(status.latestPickupDttm) : "",
      })
    }, {
      id: containerStatusColumnId.availableLoads,
      title: t('entity.containerstatus.datagrid.column.availloads', 'Avail. Loads'),
      width: width(100),
      hasMenu: true,
      measurementGroup: "load",
      getSortValue: (status) => status?.meterFill?.availableLoadCount ?? 0,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.meterFill?.availableLoadCount ?? 0,
        allowOverlay: false,
        displayData: status?.meterFill?.availableLoadCount?.toString() ?? "",
        themeOverride: status?.meterFill?.availableLoadCount ? {
          bgCell: "#01DF74FF"
        } : undefined
      })
    }, {
      id: containerStatusColumnId.availableLoadVolume,
      title: t('entity.containerstatus.datagrid.column.availloadvolume', 'Avail. Load Volume'),
      width: width(100),
      hasMenu: true,
      measurementGroup: "load",
      getSortValue: (status) => status?.meterFill?.availableLoadVolume ?? 0,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.meterFill?.availableLoadVolume ?? 0,
        allowOverlay: false,
        displayData: status?.meterFill?.availableLoadVolume?.toFixed(2) ?? "",
      })
    })

    for (const [index, cargoSubtype] of cargos.entries()) {
      const flagged = (status: ContainerStatusEntity | undefined) =>
        (index > 1 && status?.meterFill?.exceedsMaxNonPrimaryVolume) || status?.meterFill?.actualStatus === 'WARNING'

      cols.push({
        id: containerStatusColumnId.cargoVolume(cargoSubtype),
        title: cargoName(cargoSubtype) + " Volume",
        width: width(100),
        hasMenu: true,
        measurementGroup: "load",
        getSortValue: (status) => status?.getLevel(cargoSubtype, containerLevelHistoryTypes.meter)?.volume,
        getContent: (status) => ({
          kind: GridCellKind.Number,
          data: status?.getLevel(cargoSubtype, containerLevelHistoryTypes.meter)?.volume ?? Number.MIN_SAFE_INTEGER,
          allowOverlay: false,
          displayData: getDisplayableLevelValue(status?.getLevel(cargoSubtype, containerLevelHistoryTypes.meter), "volume") ?? "",
          themeOverride: flagged(status) ? CRITICAL_CELL : undefined
        })
      }, {
        id: containerStatusColumnId.cargoHeight(cargoSubtype),
        title: cargoName(cargoSubtype) + " Height",
        width: width(100),
        hasMenu: true,
        measurementGroup: "load",
        getSortValue: (status) => status?.getLevel(cargoSubtype, containerLevelHistoryTypes.meter)?.height,
        getContent: (status) => ({
          kind: GridCellKind.Number,
          data: status?.getLevel(cargoSubtype, containerLevelHistoryTypes.meter)?.height ?? Number.MIN_SAFE_INTEGER,
          allowOverlay: false,
          displayData: getDisplayableLevelValue(status?.getLevel(cargoSubtype, containerLevelHistoryTypes.meter), "height") ?? "",
          themeOverride: flagged(status) ? CRITICAL_CELL : undefined
        })
      })
    }

    cols.push({
      id: containerStatusColumnId.totalHeight,
      title: t('entity.containerstatus.datagrid.column.totalheight', 'Total Height'),
      width: width(100),
      hasMenu: true,
      measurementGroup: "load",
      getSortValue: (status) => status?.meterFill?.actualHeight,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.meterFill?.actualHeight ?? Number.MIN_SAFE_INTEGER,
        allowOverlay: false,
        displayData: getDisplayableHeightValue(status?.meterFill?.actualHeight) ?? ""
      })
    }, {
      id: containerStatusColumnId.siteAvailableLoadVolume,
      title: t('entity.containerstatus.datagrid.column.siteavailloadvolume', 'Site Avail. Load Volume'),
      defaultVisible: colOptions?.showSiteVolumeCol === true,
      width: colOptions?.showSiteVolumeCol ? width(100) : 100,
      hasMenu: true,
      measurementGroup: "load",
      getSortValue: (status) => status?.siteAvailableLoadVolume ?? 0,
      getContent: (status) => ({
        kind: GridCellKind.Number,
        data: status?.siteAvailableLoadVolume ?? 0,
        allowOverlay: false,
        displayData: status?.siteAvailableLoadVolume?.toFixed(2) ?? "",
      })
    }, {
      id: containerStatusColumnId.operatingArea,
      title: t("common.entity.operatingarea", "Operating Area"),
      width: width(100),
      hasMenu: true,
      getSortValue: (status) => status?.siteOperatingAreaName,
      getContent: (status) => ({
        kind: GridCellKind.Text,
        data: status?.siteOperatingAreaName ?? "",
        allowOverlay: false,
        displayData: status?.siteOperatingAreaName ?? "",
      })
    }, {
      id: containerStatusColumnId.assignedHauler,
      title: t('entity.containerstatus.datagrid.column.assignedcompany', 'Assigned Hauler'),
      width: width(100),
      hasMenu: true,
      getSortValue: (status) => status?.assignedCompanyName,
      getContent: (status) => ({
        kind: GridCellKind.Text,
        data: status?.assignedCompanyName ?? "",
        allowOverlay: false,
        displayData: status?.assignedCompanyName ?? "",
      })
    })

    // page-specific columns are left out entirely, not just hidden, so the column picker can't offer them
    const offered: Partial<Record<string, boolean>> = {
      [containerStatusColumnId.company]: colOptions?.showCompanyCol === true,
      [containerStatusColumnId.lastDispatch]: colOptions?.showDispatchCol === true,
      [containerStatusColumnId.lastHaul]: colOptions?.showPickupCol === true,
      [containerStatusColumnId.siteAvailableLoadVolume]: colOptions?.showSiteVolumeCol === true,
      [containerStatusColumnId.assignedHauler]: colOptions?.showAssignedHaulerCol !== false,
    }
    return cols.filter((col) => offered[col.id] !== false)
  }, [colOptions, colWidths, cargoSubtypes, t, iconSet, colorScheme, isStalePoll])

  return {
    colsMap: colsMap
  }
}
